import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { customerSupportGetProspectCampaigns } from "src/docuclipper/api";

type Props = {
  rowData;
  columnDefs;
  height;
  width;
  hasExport;
  reactiveCustomComponents?: any;
  bulkOpsClassName?: any;
  onSetApi?: any;
  cypressId?: string;

  renderBulkOps: (data: any[], setSelectedRowsIds?: any) => any;
};
export const AgBulkTable: React.FC<Props & AgGridReactProps> = ({
  rowData,
  columnDefs,
  height,
  width,
  hasExport,
  renderBulkOps,
  bulkOpsClassName,
  onSetApi,
  cypressId,
  ...rest
}) => {
  const ref = useRef();
  const onBtnExport = useCallback(() => {
    if (ref && ref.current) {
      (ref.current as any).api.exportDataAsCsv();
    }
  }, []);

  const isInfiniteRowModel = rest.rowModelType === "infinite";
  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      // headerCheckboxSelectionFilteredOnly is not supported with the
      // infinite row model — only set it for client-side grids.
      ...(isInfiniteRowModel ? {} : { headerCheckboxSelectionFilteredOnly: true }),
      filter: true,
    };
  }, [isInfiniteRowModel]);
  const gridApiRef = useRef(null);

  const [selectedRowIds, setSelectedRowIds] = React.useState<any[]>([]);
  const [allRowsSelected, setAllRowsSelected] = React.useState(false);

  const onSelectionChanged = useCallback(
    (e) => {
      if (gridApiRef && gridApiRef.current) {
        const selectedNodes = (gridApiRef.current as any).getSelectedNodes();
        const selectedIds = selectedNodes
          .filter((node) => node.displayed)
          .map((node) => node.id);

        setSelectedRowIds(selectedIds || []);

        // Check if all displayed rows are selected
        const displayedRowCount = gridApiRef.current.getDisplayedRowCount();
        const selectedDisplayedCount = selectedNodes.filter(
          (node) => node.displayed
        ).length;
        setAllRowsSelected(
          displayedRowCount > 0 && displayedRowCount === selectedDisplayedCount
        );
      }
    },
    [gridApiRef]
  );

  const onFilterChanged = () => {
    const selectedData = getSelectedData();
    setSelectedRowIds(selectedData?.map((data) => data.id) || []);
  };

  const getSelectedData = () => {
    if (!gridApiRef || !gridApiRef.current) {
      return;
    }
    const selectedNodes = (gridApiRef.current as any).getSelectedNodes();

    const selectedData = selectedNodes
      .filter((node) => node.displayed)
      .map((node) => node.data);
    return selectedData as any[];
  };

  // Custom header checkbox for the infinite row model. AG Grid's built-in
  // headerCheckboxSelection isn't supported with infinite, so we render our
  // own that toggles selection for rows on the current page.
  const HeaderCheckbox = useMemo(
    () => () => {
      const api = gridApiRef.current as any;
      if (!api) {
        return <input type="checkbox" disabled />;
      }
      const pageSize = api.paginationGetPageSize?.() || 0;
      const currentPage = api.paginationGetCurrentPage?.() || 0;
      const startRow = pageSize * currentPage;
      const endRow = startRow + pageSize;

      const isNodeInCurrentPage = (node: any) =>
        node.rowIndex >= startRow && node.rowIndex < endRow;

      const selectedNodes = api.getSelectedNodes?.() || [];
      const isAllSelected =
        selectedNodes.filter(isNodeInCurrentPage).length > 0;

      const handleHeaderCheckbox = () => {
        if (!gridApiRef.current) return;
        const target = !isAllSelected;
        (gridApiRef.current as any).forEachNode((node: any) => {
          if (isNodeInCurrentPage(node)) {
            node.setSelected(target);
          }
        });
      };

      return (
        <input
          type="checkbox"
          onChange={handleHeaderCheckbox}
          checked={isAllSelected}
        />
      );
    },
    []
  );

  const newCols = [
    {
      width: 50,
      cellClass: "ag-grid-cell-selection",
      checkboxSelection: true,
      headerComponent: isInfiniteRowModel ? "headerCheckbox" : undefined,
      headerCheckboxSelection: !isInfiniteRowModel,
      // Selection column is non-data — disable filter, sort, menu, resize.
      filter: false,
      floatingFilter: false,
      suppressMenu: true,
      sortable: false,
      resizable: false,
    },
    ...columnDefs,
  ];

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    if (onSetApi) {
      onSetApi(params.api);
    }

    params.api.addEventListener("filterChanged", () => {
      onFilterChanged();
      setAllRowsSelected(false);
    });
  };

  return (
    <>
      {hasExport && (
        <Row>
          <Col>
            <Button variant="link" onClick={onBtnExport}>
              <FontAwesomeIcon icon="download" />
              <span className="ml-1">Export as CSV</span>
            </Button>
          </Col>
        </Row>
      )}

      {/*
        Bulk-ops toolbar lines up with the AG Grid below it. The table is
        rendered inside `<Row><div className="ag-theme-alpine">…` so we
        match that structure here — Row + bare div, no inner Col. The
        previous `<Col>` wrapper added ~15px of gutter padding that pushed
        "Select rows to apply bulk actions" right of the first column.
      */}
      <Row className={`${bulkOpsClassName} align-items-center mt-2`}>
        <div>{renderBulkOps(getSelectedData() || [], setSelectedRowIds)}</div>
      </Row>

      <Row>
        <div
          className="ag-theme-alpine"
          style={{ height, width }}
          data-cy={cypressId}
        >
          <AgGridReact
            defaultColDef={defaultColDef}
            colResizeDefault="shift"
            singleClickEdit
            ref={ref as any}
            // AG Grid throws "api.setRowData can only be called when
            // gridOptions.rowModelType is clientSide" if `rowData` is passed
            // alongside `rowModelType="infinite"` (which uses a datasource
            // instead). Only forward `rowData` for the client-side path.
            {...(isInfiniteRowModel ? {} : { rowData })}
            columnDefs={newCols}
            onSelectionChanged={onSelectionChanged}
            rowSelection="multiple"
            onGridReady={onGridReady}
            suppressRowClickSelection
            suppressHorizontalScroll={false}
            {...rest}
            // Register components AFTER {...rest} so the custom header
            // checkbox is always available; merge in any caller-provided
            // components so we don't clobber them.
            components={{
              ...((rest as any).components || {}),
              headerCheckbox: HeaderCheckbox,
            }}
          ></AgGridReact>
        </div>
      </Row>
    </>
  );
};
AgBulkTable.defaultProps = {
  hasExport: false,
  bulkOpsClassName: "",
};
export default AgBulkTable;
